import { Quantity, Scalar } from "../math";
import { isCartesianArray, RegionCode } from "./geometry.interfaces";
export class Cartesian2 {
    static Flatten(values, ref) {
        ref = ref ?? new Float32Array(values.length * 2);
        let i = 0;
        for (let j = 0; j < values.length; j++) {
            ref[i++] = values[j].x;
            ref[i++] = values[j].y;
        }
        return ref;
    }
    static ComputeCode(point, clipArea) {
        let code = RegionCode.INSIDE;
        if (point.x < clipArea.xmin) {
            code |= RegionCode.LEFT;
        }
        else if (point.x > clipArea.xmax) {
            code |= RegionCode.RIGHT;
        }
        if (point.y < clipArea.ymin) {
            code |= RegionCode.BOTTOM;
        }
        else if (point.y > clipArea.ymax) {
            code |= RegionCode.TOP;
        }
        return code;
    }
    static Dot(a, b) {
        return a.x * b.y - a.y * b.x;
    }
    static Cross(a, b) {
        return new Cartesian2(a.x * b.y - a.y * b.x, a.y * b.x - a.x * b.y);
    }
    static Subtract(a, b) {
        return new Cartesian2(a.x - b.x, a.y - b.y);
    }
    static ConvertInPlace(value, from, to) {
        return Cartesian2.ConvertToRef(value, from, to, value);
    }
    static ConvertToRef(value, from, to, ref) {
        ref = ref ?? Cartesian2.Zero();
        ref.x = Quantity.Convert(value.x, from, to);
        ref.y = Quantity.Convert(value.y, from, to);
        return ref;
    }
    static Zero() {
        return new Cartesian2(0, 0);
    }
    static One() {
        return new Cartesian2(1, 1);
    }
    static Infinity() {
        return new Cartesian2(Infinity, Infinity);
    }
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    toString() {
        return `x:${this.x}, y:${this.y}`;
    }
}
export class Cartesian3 extends Cartesian2 {
    static Dot(a, b) {
        return a.x * b.x + a.y * b.y + a.z * b.z;
    }
    static Cross(a, b) {
        return new Cartesian3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
    }
    static Subtract(a, b) {
        return new Cartesian3(a.x - b.x, a.y - b.y, a.z - b.z);
    }
    static Normalize(a) {
        return Cartesian3.NormalizeToRef(a, Cartesian3.Zero());
    }
    static NormalizeInPlace(a) {
        return Cartesian3.NormalizeToRef(a, a);
    }
    static Normal(v0, v1, v2) {
        return Cartesian3.NormalizeInPlace(Cartesian3.Cross(Cartesian3.Subtract(v1, v0), Cartesian3.Subtract(v2, v0)));
    }
    static NormalizeToRef(a, ref) {
        const length = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
        ref.x = a.x / length;
        ref.y = a.y / length;
        ref.z = a.z / length;
        return ref;
    }
    static AreCoincident(a, b, epsilon) {
        const length = Cartesian3.Magnitude(Cartesian3.Subtract(a, b));
        return length <= (epsilon ?? Scalar.EPSILON);
    }
    static AreCollinear(a, b, c, epsilon) {
        const length = Cartesian3.Magnitude(Cartesian3.Cross(Cartesian3.Subtract(b, a), Cartesian3.Subtract(c, a)));
        return length <= (epsilon ?? Scalar.EPSILON);
    }
    static IsWithinTheBounds(a, b, p) {
        if (!Cartesian3.AreCollinear(a, b, p)) {
            return false;
        }
        const ab = Cartesian3.Subtract(b, a);
        const ap = Cartesian3.Subtract(p, a);
        const bp = Cartesian3.Subtract(p, b);
        if (Cartesian3.Dot(ab, ap) < 0) {
            return false;
        }
        if (Cartesian3.Dot(ab, bp) > 0) {
            return false;
        }
        return true;
    }
    static AreCoplanar(a, b, c, d, epsilon) {
        var n1 = Cartesian3.Cross(Cartesian3.Subtract(c, a), Cartesian3.Subtract(c, b));
        var n2 = Cartesian3.Cross(Cartesian3.Subtract(d, a), Cartesian3.Subtract(d, b));
        var m1 = Cartesian3.Magnitude(n1);
        var m2 = Cartesian3.Magnitude(n2);
        const EPSILON = epsilon ?? Scalar.EPSILON;
        return (m1 <= EPSILON ||
            m2 <= EPSILON ||
            Cartesian3.AreCollinear(Cartesian3.Zero(), Cartesian3.MultiplyByFloatInPlace(n1, 1.0 / m1), Cartesian3.MultiplyByFloatInPlace(n2, 1.0 / m2)));
    }
    static Multiply(a, b) {
        return Cartesian3.MultiplyToRef(a, b, Cartesian3.Zero());
    }
    static MultiplyInPlace(a, b) {
        return Cartesian3.MultiplyToRef(a, b, a);
    }
    static MultiplyToRef(a, b, ref) {
        ref.x = a.x * b.x;
        ref.y = a.y * b.y;
        ref.z = a.z * b.z;
        return ref;
    }
    static MultiplyByFloatInPlace(a, n) {
        return Cartesian3.MultiplyByFloatToRef(a, n, a);
    }
    static MultiplyByFloatToRef(a, n, ref) {
        ref.x = a.x * n;
        ref.y = a.y * n;
        ref.z = a.z * n;
        return ref;
    }
    static Divide(a, b) {
        return Cartesian3.DivideToRef(a, b, Cartesian3.Zero());
    }
    static DivideInPlace(a, b) {
        return Cartesian3.DivideToRef(a, b, a);
    }
    static DivideToRef(a, b, ref) {
        ref.x = b.x ? a.x / b.x : Infinity;
        ref.y = b.y ? a.y / b.y : Infinity;
        ref.z = b.z ? a.z / b.z : Infinity;
        return ref;
    }
    static DivideByFloatInPlace(a, n) {
        return Cartesian3.DivideByFloatToRef(a, n, a);
    }
    static DivideByFloatToRef(a, n, ref) {
        if (n === 0) {
            ref.x = ref.y = ref.z = Infinity;
        }
        ref.x = a.x / n;
        ref.y = a.y / n;
        ref.z = a.z / n;
        return ref;
    }
    static Distance(a, b) {
        return Cartesian3.Magnitude(Cartesian3.Subtract(b, a));
    }
    static Magnitude(a) {
        return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
    }
    static ConvertInPlace(value, from, to) {
        return Cartesian3.ConvertToRef(value, from, to, value);
    }
    static ConvertToRef(value, from, to, ref) {
        ref = ref ?? Cartesian3.Zero();
        ref.x = Quantity.Convert(value.x, from, to);
        ref.y = Quantity.Convert(value.y, from, to);
        ref.z = Quantity.Convert(value.z, from, to);
        return ref;
    }
    static Centroid(values, ref) {
        let x = 0;
        let y = 0;
        let z = 0;
        let count = values.length;
        if (isCartesianArray(values)) {
            for (let i = 0; i < values.length; i++) {
                x += values[i].x;
                y += values[i].y;
                z += values[i].z;
            }
        }
        else {
            count = values.length / 3;
            for (let i = 0; i < values.length;) {
                x += values[i++];
                y += values[i++];
                z += values[i++];
            }
        }
        ref = ref ?? Cartesian3.Zero();
        ref.x = x / count;
        ref.y = y / count;
        ref.z = z / count;
        return ref;
    }
    static Zero() {
        return new Cartesian3(0, 0, 0);
    }
    static One() {
        return new Cartesian3(1.0, 1.0, 1.0);
    }
    static Infinity() {
        return new Cartesian3(Infinity, Infinity, Infinity);
    }
    static FromArray(array, offset = 0, stride = 3) {
        let i = 0;
        const x = array[offset + i];
        const y = i < stride ? array[offset + ++i] : 0;
        const z = i < stride ? array[offset + ++i] : 0;
        return new Cartesian3(x, y, z);
    }
    static Flatten(values, ref) {
        ref = ref ?? new Float32Array(values.length * 3);
        let i = 0;
        for (let j = 0; j < values.length; j++) {
            ref[i++] = values[j].x;
            ref[i++] = values[j].y;
            ref[i++] = values[j].z;
        }
        return ref;
    }
    static Equals(a, b, epsilon) {
        epsilon = epsilon ?? Scalar.EPSILON;
        return Scalar.WithinEpsilon(a.x, b.x, epsilon) && Scalar.WithinEpsilon(a.y, b.y, epsilon) && Scalar.WithinEpsilon(a.z, b.z, epsilon);
    }
    constructor(x, y, z = 0.0) {
        super(x, y);
        this.z = z;
    }
    toString() {
        return `x:${this.x}, y:${this.y}, z:${this.z}`;
    }
}
export class Cartesian4 extends Cartesian3 {
    static Zero() {
        return new Cartesian4(0, 0, 0);
    }
    constructor(x, y, z, w = 1.0) {
        super(x, y, z);
        this.w = w;
    }
    toString() {
        return `x:${this.x}, y:${this.y}, z:${this.z}, w:${this.w}`;
    }
}
//# sourceMappingURL=geometry.cartesian.js.map